import { CopyOutlined, DollarOutlined, ProductOutlined, UserOutlined } from '@ant-design/icons'
import { Avatar, Button, Card, message, Space, Spin, Table, Tooltip } from 'antd'
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table'
import copy from 'copy-to-clipboard'
import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { AppAdmin } from 'api/app-admin'
import Breadcrumb from 'component/breadcrumb/breadcrumb'
import { BREADCRUMBS } from 'constants/breadcrumb'
import { FEATURE } from 'constants/feature'
import { usePermission } from 'hooks/use-permission'
import { logError } from 'utils/logger'

import './style.scss'

interface DataType {
  code: string
  key: string
  name: string
  type: string
  contractAddress: string
  country: string
  fundingTargetUI: string
  startTime: string
  endTime: string
  apy: number
  thumbnail: string
  thumbnails: string[]
  propertyItems: []
}

const P2PLending = () => {
  const [, setDataLending] = useState<DataType[]>([])
  const [filteredData, setFilteredData] = useState<DataType[]>([])
  const navigate = useNavigate()

  const [current, setCurrent] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [contractAddressSearch, setContractAddressSearch] = useState<string | undefined>(undefined)
  const [total, setTotal] = useState(0)

  const getDataLending = useCallback(
    async (pageIndex = 1, pageSize = 10) => {
      try {
        const res = await AppAdmin.getListLending(pageSize, pageIndex - 1, contractAddressSearch)
        if (res?.data.success) {
          const data = res?.data.data.map((item: any, index: number) => ({
            key: `${pageIndex}-${index}`,
            apy: item.apy,
            code: item.code,
            contractAddress: item.contractAddress,
            country: item.country,
            endTime: item.endTime,
            fundingTarget: item.fundingTarget,
            fundingTargetUI: item.fundingTargetUI,
            name: item.name,
            startTime: item.startTime,
            status: item.status,
            thumbnail: item.thumbnail,
            type: item.type,
          }))
          setDataLending(data)
          setTotal(res?.data.totalCount)
          setFilteredData(data)
        }
      } catch (error) {
        setDataLending([])
        setTotal(0)
        setFilteredData([])
        logError('Error: ', error)
      }
    },
    [contractAddressSearch]
  )

  const handleTableChange = (pagination: TablePaginationConfig) => {
    const { current: newPage, pageSize: newPageSize } = pagination
    setCurrent(newPage || 1)
    setPageSize(newPageSize || 10)
  }

  useEffect(() => {
    getDataLending()
  }, [current, pageSize, contractAddressSearch, getDataLending])

  useEffect(() => {
    setCurrent(1)
    setPageSize(10)
  }, [contractAddressSearch])

  const { canCRUD } = usePermission()
  const canEdit = canCRUD(FEATURE.P2P_LENDING)

  const columns: ColumnsType<DataType> = [
    {
      title: 'Logo',
      dataIndex: 'thumbnail',
      key: 'thumbnail',
      render: (logo) => <Avatar src={logo} size={50} />,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      sorter: (a, b) => a.type.localeCompare(b.type),
    },
    {
      title: 'Contract Address',
      dataIndex: 'contractAddress',
      key: 'contractAddress',
      render: (text) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <a
            target="_blank"
            rel="noreferrer"
            href={`https://basescan.org/address/${text}`}
          >{`${text.slice(0, 8)}...${text.slice(-8)}`}</a>
          <Tooltip title="Copy">
            <Button
              type="link"
              icon={<CopyOutlined />}
              onClick={() => {
                copy(text)
                message.success('Copied Success!')
              }}
            />
          </Tooltip>
        </div>
      ),
    },
    {
      title: 'Country',
      dataIndex: 'country',
      key: 'country',
      sorter: (a, b) => a.country.localeCompare(b.country),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) =>
        status === 1 ? (
          <span className="status-success">Active</span>
        ) : (
          <span className="status-fail">Unactive</span>
        ),
    },
    // {
    //   title: "Document Data",
    //   dataIndex: "documentData",
    //   key: "document",
    //   render: (_, record) => (
    //     <Button
    //       className="button-view-more"
    //       onClick={() => {
    //         setSelectedCode(record.code)
    //         getDocumentDetails(record.code)
    //         handleOpenDocument()
    //       }}
    //     >
    //       Add
    //     </Button>
    //   ),
    // },
    {
      title: 'Details',
      dataIndex: 'detail',
      key: 'detail',
      render: (_, record) => (
        <Space size="middle">
          <Button
            className="button-detail"
            onClick={() => {
              navigate(`/p2plending/${record.code}`)
            }}
          >
            View
          </Button>
        </Space>
      ),
    },
    // {
    //   title: "Action",
    //   key: "action",
    //   render: (_, record) => (
    //     <Space size="middle">
    //       <Button
    //         disabled={
    //           role?.includes("4") &&
    //           !role?.includes("5") &&
    //           !role?.includes("1")
    //         }
    //         className="button-edit"
    //         onClick={() => handleEdit(record)}
    //       >
    //         Edit
    //       </Button>
    //     </Space>
    //   ),
    // },
  ]
  const additionalItems = [BREADCRUMBS.P2P]

  const [data, setData] = useState<any>({})
  const [loading, setLoading] = useState(false)
  const getData = async () => {
    try {
      const res = await AppAdmin.getDashboard()
      if (res?.data.success) {
        setData(res?.data.data)
      }
    } catch (err) {
      logError(err)
    }
  }

  const getDataFirstTime = useCallback(async () => {
    setLoading(true)
    try {
      await getData()
    } catch (err) {
      logError(err)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    getDataFirstTime()
  }, [getDataFirstTime])

  return (
    <>
      <div className="main-section">
        <Breadcrumb additionalItems={additionalItems} />
        <div className="dashboard-container">
          <div className="wrap-dashboard">
            <div className="wrap-box">
              <div className="title">P2P Overview</div>
              <div className="wrap-columns">
                <div className="columns">
                  <div className="colum w-3">
                    <div className="guide-box">
                      <div className="box-content">
                        <div className="left-s">
                          <div className="txt">Total Investors</div>
                          <div className="number">
                            {loading ? (
                              <Spin />
                            ) : (
                              new Intl.NumberFormat('en-US').format(data.p2p?.totalLend)
                            )}
                          </div>
                        </div>
                        <div className="right-s">
                          <div className="icon">
                            <UserOutlined />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="colum w-3">
                    <div className="guide-box">
                      <div className="box-content">
                        <div className="left-s">
                          <div className="txt">Total Investment (USDC)</div>
                          <div className="number">
                            {loading ? (
                              <Spin />
                            ) : (
                              new Intl.NumberFormat('en-US').format(data.p2p?.totalLendAmount)
                            )}
                          </div>
                        </div>
                        <div className="right-s">
                          <div className="icon">
                            <DollarOutlined />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="colum w-3">
                    <div className="guide-box">
                      <div className="box-content">
                        <div className="left-s">
                          <div className="txt">Total Funding Target (USDC)</div>
                          <div className="number">
                            {loading ? (
                              <Spin />
                            ) : (
                              new Intl.NumberFormat('en-US').format(data.p2p?.fundingTarget)
                            )}
                          </div>
                        </div>
                        <div className="right-s">
                          <div className="icon">
                            <DollarOutlined />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="colum w-3">
                    <div className="guide-box">
                      <div className="box-content">
                        <div className="left-s">
                          <div className="txt">Total Properties</div>
                          <div className="number">
                            {loading ? (
                              <Spin />
                            ) : (
                              new Intl.NumberFormat('en-US').format(data.p2p?.remainingTarget)
                            )}
                          </div>
                        </div>
                        <div className="right-s">
                          <div className="icon">
                            <ProductOutlined />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="main-title">P2P Lending Admin</div>
        <div className="table-row">
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div className="p2p-search">
              <input
                type="text"
                placeholder="By contract address"
                value={contractAddressSearch}
                onChange={(e) => setContractAddressSearch(e.target.value)}
              />
              <Button
                type="primary"
                // onClick={() => getDataLending(current, pageSize)}
                onClick={() => {
                  setCurrent(1)
                  setPageSize(10)
                  getDataLending(1, 10)
                }}
              >
                Search
              </Button>
            </div>
            <Button
              disabled={!canEdit}
              type="primary"
              onClick={() => {
                // form.setFieldValue("latitude", DefaultLocation.lat)
                // form.setFieldValue("longitude", DefaultLocation.lng)
                // setIsEditMode(false)
                // setIsModalOpen(true)
                // form.resetFields()
                // setPropertyFields([])
                // setSelectedPropertyType(undefined)
                // setThumbnail("")
                // setThumbnails([])
                // setFileList([])
                // setSingleFile([])
                // setShowInfo(false)
                navigate('/p2plending/create')
              }}
              style={{ marginBottom: '20px' }}
            >
              Create
            </Button>
          </div>
          <Card>
            <Table
              columns={columns}
              dataSource={filteredData}
              rowKey={(record) => record.key}
              pagination={total < 11 ? false : { total, current, pageSize }}
              onChange={handleTableChange}
              scroll={{ x: 1000 }}
            />
          </Card>
        </div>
      </div>
    </>
  )
}

export default P2PLending
